// Shared outlet-reference affordance. Wherever another outlet is mentioned
// (cross-outlet reveal, grounded-claim card, contrast drawer), render its name as a
// subtle in-app link into the read-only source viewer — but ONLY when the bundle
// actually carries that outlet's article. Otherwise degrade to plain text exactly
// as before (no link affordance), so older/demo bundles look unchanged.

import { useState } from "react";
import type { SourceArticle } from "../types";
import { outletDisplay } from "../labels";

export interface SourceLinkMeta {
  from: "cross_outlet" | "grounded_claim" | "contrast_claim";
  claim_id?: string;
  highlight_id?: string;
}

export type OpenSource = (outlet: string, meta: SourceLinkMeta) => void;

export function findSource(sources: SourceArticle[] | undefined, outlet: string): SourceArticle | undefined {
  return (sources || []).find((s) => s.outlet === outlet);
}

export function SourceLink({ outlet, sources, meta, onOpen, label }: {
  outlet: string;
  sources: SourceArticle[] | undefined;
  meta: SourceLinkMeta;
  onOpen?: OpenSource;
  label?: string; // override display text (defaults to the human outlet name)
}) {
  const [hover, setHover] = useState(false);
  const src = findSource(sources, outlet);
  const text = label ?? (src?.outlet_display || outletDisplay(outlet));
  if (!src || !onOpen) return <>{text}</>; // no source article — plain text, as today
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onOpen(outlet, meta); }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      title={"Read " + (src.outlet_display || text) + "'s full article (archived study copy)"}
      style={{
        font: "inherit", color: "inherit", background: "none", border: "none", padding: 0, margin: 0,
        display: "inline", lineHeight: "inherit", cursor: "pointer",
        textDecoration: hover ? "underline" : "none", textUnderlineOffset: "2px",
        textDecorationColor: "rgba(55,53,47,.45)",
      }}>
      {text}<span aria-hidden style={{ fontSize: "0.85em", opacity: hover ? 0.8 : 0.5, marginLeft: 2 }}>↗</span>
    </button>
  );
}
